// app.jsx — root Preact tree.
// Subscribes to data-bridge for reactive re-renders when real /api/ext/*
// fetches complete. Hosts the layout switcher pill + bottom sheet, the
// search bottom sheet (geocode + geolocation + favorites), and the
// metric-tooltip toaster.

import { useEffect, useState } from 'preact/hooks';
import { readBrand, readBrandKey, DENSITY } from './theme-bridge.js';
import {
  subscribe, getRawState,
  setLocation, setUnit, useGeolocation, geocodeSuggest,
  getFavorites, devStateOverride, getActiveStateString,
  hideGeoOffer, dismissGeoOffer,
} from './data-bridge.js';
import { BottomSheet, MetricTooltip } from './atoms.jsx';
import { WxIcon, Glyph } from './icons.jsx';
import { LayoutPill, LayoutSheet, LAYOUTS } from './layout-switcher.jsx';
import { startBucketWatcher, devBucketOverride, bucketForHour } from './hero/time-bucket.js';
import { pickHeroMode, watchBattery, readHeroPref, setHeroPref } from './hero/media-policy.js';
import { VariantA } from './layouts/variant-a.jsx';
import { VariantB } from './layouts/variant-b.jsx';
import { VariantC } from './layouts/variant-c.jsx';
import { VariantD } from './layouts/variant-d.jsx';

const LS_LAYOUT  = 'weatherfcc:v32826:layout';
const LS_DENSITY = 'weatherfcc:v32826:layout-density';

function readLayout() {
  try { const v = localStorage.getItem(LS_LAYOUT); if (v && /^[ABCD]$/.test(v)) return v; } catch (_) {}
  return 'D';
}
function readDensity() {
  try { const v = localStorage.getItem(LS_DENSITY); if (v && DENSITY[v]) return v; } catch (_) {}
  return 'comfortable';
}

const byId = Object.fromEntries(LAYOUTS.map((l) => [l.id, l.Comp]));

export function App() {
  const brand = readBrand();
  const brandKey = readBrandKey();
  const [layoutId, setLayoutId] = useState(readLayout);
  const [densityId, setDensityId] = useState(readDensity);
  const density = DENSITY[densityId];

  // Re-render when data-bridge state changes.
  const [, forceTick] = useState(0);
  useEffect(() => subscribe(() => forceTick((n) => n + 1)), []);

  const state = getActiveStateString(devStateOverride());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [layoutSheetOpen, setLayoutSheetOpen] = useState(false);
  const [tip, setTip] = useState(null);

  // Time-of-day bucket — anchored to per-location sunrise/sunset, recomputed
  // every 5 min. The ?_dev_bucket=… URL param wins for QA.
  const devBucket = devBucketOverride();
  const [bucket, setBucket] = useState(devBucket || bucketForHour(new Date().getHours()));
  const bucketRaw = getRawState();
  const bucketLat = bucketRaw.location?.lat ?? null;
  const bucketLon = bucketRaw.location?.lon ?? null;
  const forecastLat = bucketRaw.forecastLocation?.lat ?? null;
  const forecastLon = bucketRaw.forecastLocation?.lon ?? null;
  const bucketLocalDate = String(bucketRaw.forecast?.current_weather?.time || '').slice(0, 10);
  const bucketTimeZone = String(bucketRaw.forecast?.timezone || '');
  const hasBucketCoordinates = bucketLat != null
    && bucketLon != null
    && forecastLat != null
    && forecastLon != null
    && Number.isFinite(Number(bucketLat))
    && Number.isFinite(Number(bucketLon))
    && Number(forecastLat) === Number(bucketLat)
    && Number(forecastLon) === Number(bucketLon);
  useEffect(() => {
    if (devBucket) return;
    return startBucketWatcher(
      () => !hasBucketCoordinates ? null : {
        lat: Number(bucketLat),
        lon: Number(bucketLon),
        localDate: bucketLocalDate,
        timeZone: bucketTimeZone,
      },
      (b) => setBucket(b)
    );
  }, [
    devBucket,
    hasBucketCoordinates,
    bucketLat,
    bucketLon,
    forecastLat,
    forecastLon,
    bucketLocalDate,
    bucketTimeZone,
  ]);

  // Hero media policy — video vs the clip's still alone ('poster'). Save-data/
  // reduced-motion/2g all force the still; the user can override via the
  // Layout sheet.
  const [heroMode, setHeroMode] = useState(() => pickHeroMode());
  useEffect(() => {
    const fix = () => setHeroMode(pickHeroMode());
    matchMedia('(prefers-reduced-motion: reduce)').addEventListener?.('change', fix);
    navigator.connection?.addEventListener?.('change', fix);
    const stopBat = watchBattery(() => setHeroMode('poster'));
    return () => {
      matchMedia('(prefers-reduced-motion: reduce)').removeEventListener?.('change', fix);
      navigator.connection?.removeEventListener?.('change', fix);
      stopBat();
    };
  }, []);
  const heroTreatment = heroMode === 'poster' ? 'poster' : 'punch';

  const Comp = byId[layoutId] || VariantD;

  const onChooseLayout = (id) => {
    if (!/^[ABCD]$/.test(id)) return;
    try { localStorage.setItem(LS_LAYOUT, id); } catch (_) {}
    setLayoutId(id);
  };

  return (
    <main data-mobile-app-root tabIndex={-1} style={{
      position: 'fixed', inset: 0, background: brand.bg, color: brand.text,
      fontFamily: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
      overflow: 'hidden', zIndex: 9999,
    }}>
      <Comp brand={brand} density={density} state={state}
            sheetOpen={sheetOpen} setSheetOpen={setSheetOpen}
            tipFn={setTip}
            heroTreatment={heroTreatment} bucket={bucket} brandKey={brandKey}/>

      {/* status-bar safe-area blur (58px per HANDOFF §9) */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 58,
        background: `linear-gradient(180deg, ${brand.bg} 0%, ${brand.bg}f0 60%, transparent 100%)`,
        backdropFilter: 'blur(14px) saturate(140%)',
        WebkitBackdropFilter: 'blur(14px) saturate(140%)',
        pointerEvents: 'none', zIndex: 70,
      }}/>

      {/* Layout switcher pill */}
      <LayoutPill brand={brand} layoutId={layoutId} onOpen={() => setLayoutSheetOpen(true)}/>

      {/* Layout switcher bottom sheet */}
      <LayoutSheet open={layoutSheetOpen} onClose={() => setLayoutSheetOpen(false)}
                   brand={brand} current={layoutId} onChoose={onChooseLayout}
                   densityId={densityId} onChooseDensity={(d) => { try { localStorage.setItem(LS_DENSITY, d); } catch (_) {} setDensityId(d); }}/>

      {/* Search bottom sheet */}
      <SearchSheet open={sheetOpen} onClose={() => setSheetOpen(false)} brand={brand}/>

      {/* Geolocation pre-permission offer — the native prompt only ever
          fires from this card's button (or the search sheet's), never
          unsolicited. mode 'search' (geo blocked/unavailable) points at the
          search sheet instead. */}
      {getRawState().geoOffer && <GeoOffer brand={brand} mode={getRawState().geoOffer}
                                           onSearch={() => { hideGeoOffer(); setSheetOpen(true); }}/>}

      {tip && <MetricTooltip tip={tip.tip} anchor={tip.anchor} brand={brand} onClose={() => setTip(null)}/>}
    </main>
  );
}

// ─── Geolocation pre-permission offer card ───────────────────────────────
// Raised by data-bridge on a first load with no stored location. mode 'geo':
// permission at 'prompt' — explains WHY before the browser asks. mode
// 'search': geolocation blocked or unavailable (privacy browsers) — same
// card, but the action opens the search sheet instead of a dead geo call.
// "Not now" persists a dismissal shared with the desktop engine.
function GeoOffer({ brand, mode, onSearch }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isSearch = mode === 'search';

  const accept = async () => {
    if (isSearch) { onSearch(); return; }
    setBusy(true); setErr('');
    try { await useGeolocation(); hideGeoOffer(); }
    catch (_) { setErr('Couldn’t get your location — you can search for a place instead.'); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="false" aria-label="See your local forecast?" style={{
      position: 'absolute', left: 14, right: 14, bottom: `calc(14px + env(safe-area-inset-bottom, 0px))`,
      zIndex: 85, maxWidth: 480, margin: '0 auto',
      background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 16,
      padding: '14px 16px', boxShadow: '0 18px 44px rgba(0,0,0,.5), 0 2px 8px rgba(0,0,0,.35)',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div aria-hidden="true" style={{
          flex: '0 0 auto', display: 'grid', placeItems: 'center', width: 38, height: 38,
          borderRadius: 12, background: `${brand.accent}29`, color: brand.accent,
        }}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-6.5-5.4-6.5-10.2A6.5 6.5 0 0 1 12 4.5a6.5 6.5 0 0 1 6.5 6.3C18.5 15.6 12 21 12 21z"/><circle cx="12" cy="10.8" r="2.3"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: brand.text }}>See your local forecast?</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.45, color: brand.textDim, marginTop: 3 }}>
            {isSearch
              ? 'You’re viewing New York right now. Your browser doesn’t share location — search your city once and we’ll remember it for next time.'
              : 'You’re viewing New York right now. Tap below and your browser will ask permission to share your location — it’s only used to fetch the weather near you.'}
          </div>
        </div>
      </div>
      {err && <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(248,113,113,.12)', color: '#fca5a5', fontSize: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={accept} disabled={busy} className="wx-tap" style={{
          flex: 1.4, padding: '10px 12px', borderRadius: 10, background: brand.accent, color: brand.bg,
          border: 'none', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
          cursor: busy ? 'wait' : 'pointer', opacity: busy ? .7 : 1,
        }}>{isSearch ? 'Search for a city' : (busy ? 'Locating…' : 'Use my location')}</button>
        <button onClick={dismissGeoOffer} disabled={busy} className="wx-tap" style={{
          flex: 1, padding: '10px 12px', borderRadius: 10, background: 'transparent', color: brand.textDim,
          border: `1px solid ${brand.line}`, fontWeight: 600, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
        }}>Not now</button>
      </div>
    </div>
  );
}

// ─── Search bottom sheet ─────────────────────────────────────────────────
function SearchSheet({ open, onClose, brand }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const favorites = getFavorites();
  const state = getRawState();

  // Debounced typeahead — fires after 250ms idle.
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const hits = await geocodeSuggest(q.trim());
        if (!cancelled) setResults(hits);
      } catch (_) { if (!cancelled) setResults([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [q, open]);

  const pick = (r) => {
    const name = r.name + (r.admin1 ? `, ${r.admin1}` : '') + (r.country_code ? ` · ${r.country_code}` : '');
    setLocation({ name, lat: r.latitude || r.lat, lon: r.longitude || r.lon });
    setQ(''); setResults([]); onClose();
  };

  const tryGeo = async () => {
    setBusy(true); setErr('');
    try { await useGeolocation(); onClose(); }
    catch (e) { setErr(e && e.message ? e.message : 'Couldn’t get location'); }
    finally { setBusy(false); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Search locations" brand={brand} height="78%">
      <div style={{ padding: '8px 16px 12px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          borderRadius: 12, background: brand.panelHi, border: `1px solid ${brand.line}`,
        }}>
          <Glyph.Search s={16} c={brand.textDim}/>
          <input
            aria-label="City, ZIP code, or address"
            placeholder="City, ZIP code, or address…"
            value={q}
            onInput={(e) => setQ(e.target.value)}
            autoFocus={open}
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                     color: brand.text, fontSize: 15, fontFamily: 'inherit' }}/>
          {q && <button type="button" aria-label="Clear search" onClick={() => { setQ(''); setResults([]); }} style={{ background: 'transparent', border: 'none', color: brand.textDim, fontSize: 18, padding: 0, cursor: 'pointer' }}>×</button>}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={tryGeo} disabled={busy} style={{
            flex: 1, padding: '10px 12px', borderRadius: 10, background: brand.accent, color: brand.bg,
            border: 'none', fontWeight: 600, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: busy ? .7 : 1,
          }}>{busy ? 'Locating…' : 'Use my location'}</button>
          <div style={{ flex: 1, padding: '10px 12px', borderRadius: 10, background: 'transparent', color: brand.textDim, border: `1px solid ${brand.line}`, fontSize: 12, lineHeight: 1.3, textAlign: 'center' }}>
            {state.loading ? 'Loading…' : (state.location ? `Now: ${state.location.name}` : 'No location yet')}
          </div>
        </div>
        {err && <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(248,113,113,.12)', color: '#fca5a5', fontSize: 12 }}>{err}</div>}
      </div>

      {results.length > 0 && (
        <div style={{ padding: '4px 16px 12px' }}>
          <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em', padding: '12px 4px 6px' }}>Matches</div>
          {results.map((r, i) => (
            <button key={i} onClick={() => pick(r)} className="wx-tap" style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '10px 6px',
              background: 'transparent', border: 'none', borderBottom: `1px solid ${brand.line}`,
              color: brand.text, cursor: 'pointer',
            }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
              <div style={{ fontSize: 11, color: brand.textDim, marginTop: 1 }}>
                {[r.admin1, r.admin2, r.country].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}

      {results.length === 0 && (
        <div style={{ padding: '4px 16px 12px' }}>
          <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em', padding: '12px 4px 6px' }}>Favorites</div>
          {favorites.length === 0 && (
            <div style={{ fontSize: 13, color: brand.textDim, padding: '8px 4px' }}>No saved favorites yet. Search above or use your location.</div>
          )}
          {favorites.map((f) => (
            <button key={f.id}
              onClick={() => f.lat != null && setLocation({ name: f.name || f.label, lat: f.lat, lon: f.lon }) || onClose()}
              className="wx-tap"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 6px',
                width: '100%', background: 'transparent', border: 'none',
                borderBottom: `1px solid ${brand.line}`, textAlign: 'left',
                color: brand.text, cursor: 'pointer',
              }}>
              <WxIcon kind={f.icon} size={32}/>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: brand.text }}>{f.label}</div>
                <div style={{ fontSize: 12, color: brand.textDim, marginTop: 1 }}>{f.region || ''}{f.region && f.cond ? ' · ' : ''}{f.cond || ''}</div>
              </div>
              {f.temp ? <div style={{ fontSize: 18, fontWeight: 600, color: brand.text, fontVariantNumeric: 'tabular-nums' }}>{f.temp}°</div> : null}
            </button>
          ))}
        </div>
      )}
    </BottomSheet>
  );
}
